import { Button, CircularProgress } from '@material-ui/core';
import { Print } from '@material-ui/icons';
import purchasingApi from 'api/query/purchasing';
import { OrderInvoiceModel, OrderItemModel } from 'api/query/purchasing/models';
import React, { useEffect, useState } from 'react';
import './invoice.scss';

interface Props {
  invoice?: OrderInvoiceModel | null;
}

const formatCurrency = (value?: number | null) => {
  if (value == null) return '';
  return value.toFixed(2);
};

const formatDate = (d?: Date | null) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-AU');
  } catch (e) {
    return String(d);
  }
};

const Invoice: React.FC<Props> = ({ invoice }) => {
  const [localInvoice, setLocalInvoice] = useState<OrderInvoiceModel | null>(invoice ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    if (invoice) {
      setLocalInvoice(invoice);
      return () => { mounted = false; };
    }

    // read orderId from query string and fetch invoice
    try {
      const params = new URLSearchParams(window.location.search);
      const orderIdRaw = params.get('orderId');
      if (!orderIdRaw) return () => { mounted = false; };
      const orderIdNum = Number(orderIdRaw);
      if (Number.isNaN(orderIdNum)) {
        setLoadError('Invalid order id');
        return () => { mounted = false; };
      }

      const fetchInvoice = async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
          // fetch raw payload directly so we can map property names reliably
          const result = await purchasingApi.getInvoice(orderIdNum);

          if (mounted) setLocalInvoice(result);
        } catch (e) {
          console.error('Failed to load invoice by orderId:', e);
          if (mounted) setLoadError((e as any)?.message || 'Failed to load invoice');
        } finally {
          if (mounted) setIsLoading(false);
        }
      };

      fetchInvoice();
    } catch (e) {
      console.error('Failed to read orderId from URL', e);
    }

    return () => { mounted = false; };
  }, [invoice]);

  const data = invoice ?? localInvoice;
  const company = data?.company;

  return (
    <>
      <div className="invoice-actions no-print">
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.print()}
          id="printInvoice"
          startIcon={isLoading ? <CircularProgress color="inherit" size={20} /> : <Print />}
          disabled={isLoading || !data}
        >
          {isLoading ? 'Loading...' : 'Print'}
        </Button>
      </div>
      <div id="invoice-content" data-order-id={data?.orderId} className={`invoice-container ${isLoading ? '' : 'visible'}`}>
          
        {!isLoading && data && (
          <>
            <div className="invoice-header">
              <table className='table'>
                <tr>
                  <td>
                    <table className='table'>
                      <tr>
                        <td className="sheep-genetics-name">Sheep Genetics</td>
                      </tr>
                      <tr>
                        <td className="sheep-genetics-details">
                          The Short Run
                          <br />UNE ARMIDALE NSW 2351
                          <br />PH: +61 2 8055 1818
                          <br />ABN 39 081 678 364
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td className='logo-cell'><img src="/images/mla_logo_home.svg" alt="MLA Logo" className="logo" /></td>
                </tr>
              </table>

            </div>

            <div className="divider" />

            <div>
              <table className='table'>
                <tr>
                  <td className="tax-invoice-title center" colSpan={2}>Tax Invoice - Receipt</td>
                </tr>
                <tr>
                  <td>
                    <table className='table'>
                      <tr>
                        <td className="customer-label">Attention:</td>
                      </tr>
                      <tr>
                        <td className="customer-details">
                          {company?.companyName ?? 'Customer Name'}
                          {company?.addressLine1 ? <><br />{company.addressLine1}</> : null}
                          {company?.addressLine2 ? <><br />{company.addressLine2}</> : null}
                          {company?.city ? <><br />{company.city} {company.state} {company.postalCode}</> : null}
                        </td>
                      </tr> 
                    </table>
                  </td>
                  <td className='metadata-cell'>
                    <table className="metadata-table table">
                      <tr>
                        <th className='align-right'>Customer No:</th>
                        <td className='align-right'>{company?.customerId ?? company?.id ?? ' '}</td>
                      </tr>
                      <tr>
                        <th className='align-right'>Invoice No:</th>
                        <td className='align-right'>{data?.orderNumber ?? ' '}</td>
                      </tr>
                      <tr>
                        <th className='align-right'>Invoice Date:</th>
                        <td className='align-right'>{formatDate(data?.orderDate)}</td>
                      </tr>
                      <tr>
                        <th className='align-right'>Status:</th>
                        <td className='align-right'>{data?.payments?.[0]?.statusName ?? ' '}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Description</th>
                    <th className="align-right">Unit Cost</th>
                    <th className="align-right">Qty.</th>
                    <th className='align-right'>Sub Total</th>
                    <th className='align-right'>Tax</th>
                    <th className="align-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items && data.items.length > 0 ? (
                    data.items!.map((it: OrderItemModel, idx) => (
                      <tr key={it.id ?? idx}>
                        <td>{it.linkedItemId ?? ''}</td>
                        <td>{it.description ?? ''}</td>
                        <td className="align-right">{formatCurrency(it.unitCost)}</td>
                        <td className="align-right">{it.quantity ?? ''}</td>
                        <td className="align-right">{formatCurrency(it.subTotal)}</td>
                        <td className="align-right">{formatCurrency(it.tax)}</td>
                        <td className="align-right">{formatCurrency(it.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>No items</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="totals-labels no-border" colSpan={6}>
                      <div>Net Amount: AUD</div>
                      <div>GST Amount: AUD</div>
                      <div className="total-row">Invoice Total: AUD</div>
                    </td>
                    <td className="totals-values">
                      <div>{formatCurrency(data?.subTotal)}</div>
                      <div>{formatCurrency(data?.gst)}</div>
                      <div className="total-row">{formatCurrency(data?.total)}</div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            <div className='copy-section center' style={{ borderBottom:'1px solid #000000;'}}>
              <div className='copy'>
                <div className="align-center">Payment has been received by Meat and Livestock Australia Limited ABN 34 134 745 038 for the Sheep Genetics' Program.</div>
                <div className="align-center">For order queries email <a href="mailto:info@sheepgenetics.org.au">info@sheepgenetics.org.au</a> or call +61 2 8055 1818.</div>
              </div>
            </div>

            <div className='divider-dashed'></div>

            <div className='copy-section center'>
              <div className='copy'>
                <div className="align-center"><strong>DO NOT SEND ADDITIONAL PAYMENT</strong></div>
                <div className="align-center"><strong>PLEASE RETAIN A COPY OF THIS TAX INVOICE FOR YOUR RECORDS</strong></div>
              </div>
            </div>

            <div className="invoice-footer">
              <div className="footer-divider" />
              <div className="footer-text">Sheep Genetics is a program of Meat &amp; Livestock Australia Limited.</div>
            </div>
          </>
          )}
      </div>
    </>
  );
};

export default Invoice;
